/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: ObjectComponent
 * Reason: ObjectBaseComponent for the sm.SmSignatureProfile configuration entity. The profile carries
 * two child entity types - sm.SmSignatureProfileRole and sm.SmSignatureProfileChannelType - which makes
 * it a complex configuration entity: it is edited on a dedicated object page with left navigation, never
 * as flat tabs on the config page.
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {Subscription} from 'rxjs';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../core/shared/misc/sorting';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';
import {SmSignatureProfileDto} from '../../../model/dto/entity/sm-signature-profile.dto';
import {SmExternalSigningModeEnum} from '../../../model/enum/sm-external-signing-mode.enum';
import {SmEvidenceLevelEnum} from '../../../model/enum/sm-evidence-level.enum';

@Component({
    selector: 'mvs-sm-signature-profile-object',
    templateUrl: './sm-signature-profile-object.component.html',
    styleUrls: ['./sm-signature-profile-object.component.scss'],
    standalone: false
})
export class SmSignatureProfileObjectComponent
    extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    declare dto: SmSignatureProfileDto;

    /**
     * Exposed for the template, which cannot reach a TypeScript enum on its own. Used by the header
     * tag to tell the three external signing states apart - not offered, in preparation, released.
     */
    protected readonly SmExternalSigningModeEnum = SmExternalSigningModeEnum;

    /**
     * Exposed for the header, which compares the profile's in-person demand against the enum MEMBER.
     *
     * The template cannot name the enum itself, and it must not fall back to a truthiness test: the
     * level is an {@code AlphaBaseEnum} and arrives as an ordinal, so {@code ses} is 0 and would read
     * as "nothing configured" by accident rather than by rule.
     */
    protected readonly SmEvidenceLevelEnum = SmEvidenceLevelEnum;

    navigationItems: NavigationItem[] = [
        {
            label: 'Grunddaten',
            action: 'details',
            tooltip: 'Dokumenttyp, Ableitung, Stempelung und die Freigabe für die externe Signatur',
            icon: 'fa-solid fa-circle-info',
            toggleable: false,
            default: true
        },
        {
            label: 'Unterzeichnerrollen',
            action: 'roles',
            tooltip: 'Rollen, die im Dokument dieses Profils unterschreiben',
            icon: 'fa-solid fa-users',
            toggleable: false,
            default: false
        },
        {
            label: 'Erlaubte Kanalvarianten',
            action: 'channelTypes',
            tooltip: 'Kanaltypen, über die ein Dokument dieses Profils unterschrieben werden darf',
            icon: 'fa-solid fa-tower-broadcast',
            toggleable: false,
            default: false
        }
    ];

    detailWidget: WidgetData;

    roleWidget: WidgetData;

    channelTypeWidget: WidgetData;

    private queryParamSubscription: Subscription;

    constructor(
        protected override coreService: MvsCoreService,
        protected override messageService: MvsMessageService,
        protected override confirmationService: ConfirmationService,
        protected override observerService: ObserverService,
        private activatedRoute: ActivatedRoute
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscribeToNavItemQueryParam();
    }

    override onObjectChanged(): void {
        this.setPageTitle(this.dto?.name || 'Signaturprofil');

        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }

        this.onNavigationItems.emit(this.navigationItems);

        this.createChildWidgets();
    }

    /**
     * Subscribe to the navItem query parameter so a caller can deep link into one section of the
     * profile. ObjectBaseComponent does not read ?navItem= on its own.
     */
    private subscribeToNavItemQueryParam(): void {
        this.queryParamSubscription = this.activatedRoute.queryParams.subscribe(params => {
            const navItemAction = params['navItem'];
            if (navItemAction && this.navigationItems) {
                const found = this.navigationItems.find(item => item.action === navItemAction);
                if (found) {
                    this.activeNavigationItems = found;
                }
            }
        });
    }

    /**
     * Create the detail widget and the widgets of the two child entity types.
     */
    private createChildWidgets(): void {
        const profileId = this.objectIdentifier.objectId;

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Metadata driven form of the sm.SmSignatureProfile record itself. It carries the
         * external signing settings - externalSigningMode, linkValidityDays, consentTextVersion,
         * consentText, signingLinkTargetUrl, maxDocumentPages and maxDocumentSizeKb - which reach the
         * form through
         * the backend field metadata rather than through hand written controls.
         */
        this.detailWidget = WidgetFactory.createWidgetEntityData(
            'sm.signature.profile.object.details',
            'Grunddaten des Signaturprofils',
            'sm.SmSignatureProfile',
            profileId
        );

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Table of the sm.SmSignatureProfileRole rows of this profile, the first child entity
         * type. A role states how one signature field of the document is read: role key, signing order,
         * mandatory mode and the accompanying place and date fields.
         */
        this.roleWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sm.signature.profile.object.roles',
            'Unterzeichnerrollen',
            'sm.SmSignatureProfileRole',
            [FilterCriteria.create('e.profileId', FilterCriteria.cOperatorEqual, profileId)],
            [new Sorting('e.signingOrder', true)]
        );
        const roleFields: WidgetToolbarCreateInterface[] = [{fieldName: 'profileDtoId', fieldValue: profileId}];
        this.roleWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(roleFields);

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Runtime
         * Reason: Table of the sm.SmSignatureProfileChannelType rows of this profile, the second child
         * entity type. Each row releases one channel type for this document type; the order decides how
         * the variants are offered to the caseworker.
         */
        this.channelTypeWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'sm.signature.profile.object.channel.types',
            'Erlaubte Kanalvarianten',
            'sm.SmSignatureProfileChannelType',
            [FilterCriteria.create('e.signatureProfileId', FilterCriteria.cOperatorEqual, profileId)],
            [new Sorting('e.sortOrder', true)]
        );
        const channelTypeFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'signatureProfileDtoId', fieldValue: profileId}
        ];
        this.channelTypeWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(channelTypeFields);
    }

    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);
    }

    override ngOnDestroy(): void {
        this.queryParamSubscription?.unsubscribe();
        super.ngOnDestroy();
    }
}
